/**
 * Default Model Sheet Component
 * 
 * A compact sheet that allows users to change their default AI provider and model
 * from within the Threads interface. Uses the same underlying preferences as the
 * User Profile / User Preferences pages.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles } from 'lucide-react';
import { DEFAULT_AI_MODEL, DEFAULT_AI_PROVIDER } from '@/config/aiDefaults';

interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: Array<{
    id: string;
    name: string;
    description: string;
    available: boolean;
  }>;
  available: boolean;
}

interface UserPreferences {
  subscriptionTier: string;
  advancedMode: boolean;
  defaultProvider: string;
  defaultModel: string;
}

interface DefaultModelSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DefaultModelSheet({ open, onOpenChange }: DefaultModelSheetProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch available providers
  const { data: providers, isLoading: loadingProviders } = useQuery<ChatProvider[]>({
    queryKey: ['/api/chat/providers'],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch user preferences
  const { data: preferences, isLoading: loadingPrefs } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (updates: Partial<UserPreferences>) => {
      return await apiRequest('PATCH', '/api/user/preferences', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      toast({
        title: 'Default model updated',
        description: 'Your default AI model has been saved.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update preferences',
        variant: 'destructive',
      });
    },
  });

  const currentProvider = preferences?.defaultProvider || DEFAULT_AI_PROVIDER;
  const currentModel = preferences?.defaultModel || DEFAULT_AI_MODEL;

  // Get available models for selected provider
  const getAvailableModels = (provider: string) => {
    if (!providers) return [];
    const providerData = providers.find((p) => p.provider === provider);
    return providerData?.models || [];
  };

  // Handle provider change
  const handleProviderChange = (newProvider: string) => {
    const models = getAvailableModels(newProvider);
    const defaultModel = models.length > 0 ? models[0].id : DEFAULT_AI_MODEL;

    updatePreferencesMutation.mutate({
      defaultProvider: newProvider,
      defaultModel: defaultModel,
    });
  };

  // Handle model change
  const handleModelChange = (newModel: string) => {
    updatePreferencesMutation.mutate({
      defaultModel: newModel,
    });
  };

  const isLoading = loadingProviders || loadingPrefs;
  const isSaving = updatePreferencesMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[320px] sm:w-[380px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Default AI Model
          </SheetTitle>
          <SheetDescription>
            Choose your preferred AI provider and model for new conversations.
            This does not affect the orchestrator model.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select
                  value={currentProvider}
                  onValueChange={handleProviderChange}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {providers?.map((provider) => (
                      <SelectItem
                        key={provider.provider}
                        value={provider.provider}
                        disabled={!provider.available}
                      >
                        <div className="flex items-center gap-2">
                          <span>{provider.name || provider.provider}</span>
                          {!provider.available && (
                            <Badge variant="secondary">
                              Unavailable
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Model</Label>
                <Select
                  value={currentModel}
                  onValueChange={handleModelChange}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableModels(currentProvider).map((model) => (
                      <SelectItem
                        key={model.id}
                        value={model.id}
                        disabled={!model.available}
                      >
                        <div className="flex flex-col">
                          <span>{model.name}</span>
                          {model.description && (
                            <span className="text-sm text-gray-500">
                              {model.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                <p className="font-medium mb-1">Note</p>
                <p>
                  This setting controls the default model for long-form
                  completions and direct conversations. The Haiku orchestrator
                  model used for agentic flows is separate and unaffected.
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default DefaultModelSheet;

